import React, { useCallback, useEffect, useState } from 'react';
import { BoxIcon, SearchIcon, PlusIcon, DollarSignIcon, ClockIcon, EditIcon, TrashIcon, UserIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { EmptyBoxPurchase } from '../types';
import { supabaseAdmin } from '../lib/supabase';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { useAuth } from '../context/AuthContext';
const VENDEDORES = ['Juan Pérez', 'María García', 'Carlos Ruiz', 'Ana López'];
const FARMACIAS = ['Farmacia Central', 'Farmacia El Sol', 'Farmacia La Salud', 'Farmacia Bienestar', 'Farmacia Nueva Vida'];
const INITIAL_PURCHASES: EmptyBoxPurchase[] = [{
  id: '1',
  farmacia: 'Farmacia Central',
  vendedor: 'Juan Pérez',
  fecha: new Date('2024-02-28'),
  cantidadCajas: 50,
  precioPorCaja: 25,
  total: 1250,
  estado: 'pagado'
}, {
  id: '2',
  farmacia: 'Farmacia El Sol',
  vendedor: 'María García',
  fecha: new Date('2024-02-27'),
  cantidadCajas: 30,
  precioPorCaja: 25,
  total: 750,
  estado: 'pendiente'
}, {
  id: '3',
  farmacia: 'Farmacia La Salud',
  vendedor: 'Carlos Ruiz',
  fecha: new Date('2024-02-26'),
  cantidadCajas: 100,
  precioPorCaja: 22,
  total: 2200,
  estado: 'pagado'
}, {
  id: '4',
  farmacia: 'Farmacia Bienestar',
  vendedor: 'Juan Pérez',
  fecha: new Date('2024-02-25'),
  cantidadCajas: 45,
  precioPorCaja: 25,
  total: 1125,
  estado: 'pagado'
}, {
  id: '5',
  farmacia: 'Farmacia Nueva Vida',
  vendedor: 'Ana López',
  fecha: new Date('2024-02-24'),
  cantidadCajas: 20,
  precioPorCaja: 25,
  total: 500,
  estado: 'pendiente'
}];
type FormData = {
  farmacia: string;
  vendedor: string;
  fecha: string;
  cantidadCajas: string;
  precioPorCaja: string;
  estado: EmptyBoxPurchase['estado'];
  nota: string;
};
const emptyForm: FormData = {
  farmacia: FARMACIAS[0],
  vendedor: VENDEDORES[0],
  fecha: new Date().toISOString().split('T')[0],
  cantidadCajas: '',
  precioPorCaja: '',
  estado: 'pendiente',
  nota: ''
};
export function CajaVaciaPage() {
  const {
    addActivityLog
  } = useAuth();
  const [purchases, setPurchases] = useState<EmptyBoxPurchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendedorFilter, setVendedorFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<EmptyBoxPurchase | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const filteredPurchases = purchases.filter(p => {
    const matchesSearch = p.farmacia.toLowerCase().includes(searchQuery.toLowerCase()) || p.vendedor.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.estado === statusFilter;
    const matchesVendedor = vendedorFilter === 'all' || p.vendedor === vendedorFilter;
    return matchesSearch && matchesStatus && matchesVendedor;
  });
  // Sorting setup
  const {
    items: sortedPurchases,
    requestSort,
    sortConfig
  } = useSortableData(filteredPurchases, {
    farmacia: p => p.farmacia,
    vendedor: p => p.vendedor,
    fecha: p => new Date(p.fecha).getTime(),
    cantidadCajas: p => p.cantidadCajas,
    precioPorCaja: p => p.precioPorCaja,
    total: p => p.total,
    estado: p => p.estado
  });
  // Default sort by date descending if no sort config
  const finalPurchases = sortConfig ? sortedPurchases : [...sortedPurchases].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalCajas = finalPurchases.reduce((sum, p) => sum + p.cantidadCajas, 0);
  const inversionTotal = finalPurchases.reduce((sum, p) => sum + p.total, 0);
  const comprasPendientes = finalPurchases.filter(p => p.estado === 'pendiente').length;
  const computedTotal = (Number(formData.cantidadCajas) || 0) * (Number(formData.precioPorCaja) || 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const openAddModal = () => {
    setEditingPurchase(null);
    setFormData(emptyForm);
    setIsModalOpen(true);
  };
  const openEditModal = (purchase: EmptyBoxPurchase) => {
    setEditingPurchase(purchase);
    setFormData({
      farmacia: purchase.farmacia,
      vendedor: purchase.vendedor,
      fecha: new Date(purchase.fecha).toISOString().split('T')[0],
      cantidadCajas: String(purchase.cantidadCajas),
      precioPorCaja: String(purchase.precioPorCaja),
      estado: purchase.estado,
      nota: purchase.nota || ''
    });
    setIsModalOpen(true);
  };
  useEffect(() => {
    loadPurchases();
  }, []);
  // Auto-refresh when empty_box_purchases change in Supabase
  const loadPurchasesCallback = useCallback(() => {
    loadPurchases();
  }, []);
  useRealtimeRefresh(['empty_box_purchases'], loadPurchasesCallback);
  const mapPurchase = (row: any): EmptyBoxPurchase => ({
    id: row.id,
    farmacia: row.farmacia,
    vendedor: row.vendedor,
    fecha: new Date(row.fecha),
    cantidadCajas: row.cantidad_cajas,
    precioPorCaja: row.precio_por_caja,
    total: row.total,
    estado: row.estado,
    nota: row.nota || undefined
  });
  const loadPurchases = async () => {
    setIsLoading(true);
    try {
      const {
        data
      } = await supabaseAdmin.from('empty_box_purchases').select('*').order('fecha', {
        ascending: false
      });
      if (data) setPurchases(data.map(mapPurchase));
    } finally {
      setIsLoading(false);
    }
  };
  const handleSave = async () => {
    if (!formData.cantidadCajas || !formData.precioPorCaja) return;
    setIsSaving(true);
    try {
      const payload = {
        farmacia: formData.farmacia,
        vendedor: formData.vendedor,
        fecha: new Date(formData.fecha).toISOString(),
        cantidad_cajas: Number(formData.cantidadCajas),
        precio_por_caja: Number(formData.precioPorCaja),
        total: computedTotal,
        estado: formData.estado,
        nota: formData.nota || null
      };
      if (editingPurchase) {
        const {
          data
        } = await supabaseAdmin.from('empty_box_purchases').update(payload).eq('id', editingPurchase.id).select().single();
        if (data) setPurchases(prev => prev.map(p => p.id === editingPurchase.id ? mapPurchase(data) : p));
        addActivityLog('Editó compra caja vacía', `Farmacia: ${formData.farmacia} | Cajas: ${formData.cantidadCajas} | Total: RD${computedTotal.toFixed(2)}`);
      } else {
        const {
          data
        } = await supabaseAdmin.from('empty_box_purchases').insert(payload).select().single();
        if (data) setPurchases(prev => [mapPurchase(data), ...prev]);
        addActivityLog('Creó compra caja vacía', `Farmacia: ${formData.farmacia} | Cajas: ${formData.cantidadCajas} | Total: RD${computedTotal.toFixed(2)}`);
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingPurchase) return;
    addActivityLog('Eliminó compra caja vacía', `Farmacia: ${editingPurchase.farmacia} | Total: RD${editingPurchase.total.toFixed(2)}`);
    await supabaseAdmin.from('empty_box_purchases').delete().eq('id', editingPurchase.id);
    setPurchases(prev => prev.filter(p => p.id !== editingPurchase.id));
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-457" data-mp="3ieq2mw8v6w0j">
      <PageHeader title="Compra de Caja Vacía" subtitle="Registro de compra de cajas a farmacias" icon={<BoxIcon className="w-6 h-6" data-id="element-459" data-mp="3dgetknqe8pjd" />} data-id="element-458" data-mp="3idx2wf8bp6rw">
        
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-461" data-mp="evwux02rp1ea" />} onClick={openAddModal} data-id="element-460" data-mp="exhnik5iua9i">
          
          Registrar Compra
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-stagger-1" data-id="element-462" data-mp="575630t06x7l">
        <StatsCard title="Total Cajas" value={totalCajas} icon={<BoxIcon className="w-5 h-5" data-id="element-464" data-mp="70pzbw0c4hw" />} variant="primary" data-id="element-463" data-mp="5529tz19c7uf" />
        
        <StatsCard title="Inversión Total" value={formatCurrency(inversionTotal)} icon={<DollarSignIcon className="w-5 h-5" data-id="element-466" data-mp="xpak0ojsudlz" />} variant="success" data-id="element-465" data-mp="xn65zds6ry7z" />
        
        <StatsCard title="Pagos Pendientes" value={comprasPendientes} icon={<ClockIcon className="w-5 h-5" data-id="element-468" data-mp="116f0xmdlflpz" />} variant="warning" data-id="element-467" data-mp="svemfn7g6src" />
        
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 animate-stagger-2" data-id="element-469" data-mp="1580bv26khpxj">
        <div className="relative flex-1" data-id="element-470" data-mp="1a5iiun10xv2w">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-471" data-mp="1a4gz7u5y009k" />
          <input type="text" placeholder="Buscar por farmacia o vendedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-472" data-mp="1f6pp2hniqm7g" />
          
        </div>
        <select value={vendedorFilter} onChange={e => setVendedorFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-473" data-mp="1syvylkw61uxx">
          
          <option value="all" data-id="element-474" data-mp="27ullrmd38e48">Todos los vendedores</option>
          {VENDEDORES.map(v => <option key={v} value={v} data-id="element-475" data-mp="14391can72ncm">
              {v}
            </option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-476" data-mp="183jf49h8ai21">
          
          <option value="all" data-id="element-477" data-mp="1hvxmbhr2sgvl">Todos los estados</option>
          <option value="pendiente" data-id="element-478" data-mp="1mtqps30zuws6">Pendiente</option>
          <option value="pagado" data-id="element-479" data-mp="1mva77wydzdvj">Pagado</option>
        </select>
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-480" data-mp="1ru4xfjyoo4po">
        {filteredPurchases.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-481" data-mp="1vrj71cqa0ei7">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-482" data-mp="20rnqnbmhdpcn">
              <BoxIcon className="w-8 h-8 text-slate-400" data-id="element-483" data-mp="1gwa5yzh9yp1" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-484" data-mp="6dw9lr1to376">
              No hay registros
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-485" data-mp="3v1cdlo6yn1da">
              No se encontraron compras con los filtros aplicados
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-486" data-mp="3q8139d2xw2gm">
            <table className="w-full" data-id="element-487" data-mp="3lbsv4jn5t9iy">
              <thead data-id="element-488" data-mp="3l8okb3f6vfjb">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-489" data-mp="3gbo96e3hfmlc">
                  <SortableTableHeader label="Farmacia" sortKey="farmacia" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-490" data-mp="3g9ldpejk9198" />
                
                  <SortableTableHeader label="Vendedor" sortKey="vendedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-491" data-mp="cvqym41escmh" />
                
                  <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-492" data-mp="3tq2jahwvwme" />
                
                  <SortableTableHeader label="Cajas" sortKey="cantidadCajas" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-493" data-mp="2ojfkvxhtq0ju" />
                
                  <SortableTableHeader label="Precio/Caja" sortKey="precioPorCaja" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-494" data-mp="2uzfq4zhvknwn" />
                
                  <SortableTableHeader label="Total" sortKey="total" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-495" data-mp="2l0zwaorgr0jg" />
                
                  <SortableTableHeader label="Estado" sortKey="estado" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-496" data-mp="2c3fr54qhck3k" />
                
                  <th className="px-5 py-4 w-20" data-id="element-497" data-mp="22cl3px1hzegj"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-498" data-mp="1octxwjl4x4dm">
                {finalPurchases.map(purchase => <tr key={purchase.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-499" data-mp="1sclz2b87888y">
                
                    <td className="px-5 py-4 font-medium text-slate-900" data-id="element-500" data-mp="19q4wudb8ij54">
                      {purchase.farmacia}
                    </td>
                    <td className="px-5 py-4" data-id="element-501" data-mp="1iq9rrnz8rp2e">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-502" data-mp="1ir2wkjen44hw">
                        <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-503" data-mp="1dq5gccd6xkj1" />
                        {purchase.vendedor}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600" data-id="element-504" data-mp="1npw8i7ea3nfy">
                      {formatDate(purchase.fecha)}
                    </td>
                    <td className="px-5 py-4 text-center font-medium text-slate-900" data-id="element-505" data-mp="21g9i0e6tja7e">
                      {purchase.cantidadCajas}
                    </td>
                    <td className="px-5 py-4 text-right text-sm text-slate-600" data-id="element-506" data-mp="2befhkp55k493">
                      {formatCurrency(purchase.precioPorCaja)}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-slate-900" data-id="element-507" data-mp="26e17l1jzqk8f">
                      {formatCurrency(purchase.total)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-508" data-mp="2ui0t8h2t70ld">
                      {purchase.estado === 'pagado' ? <Badge variant="success" data-id="element-509" data-mp="2zblinkqc6m8y">Pagado</Badge> : <Badge variant="warning" data-id="element-510" data-mp="2zcmv9tn03gss">Pendiente</Badge>}
                    </td>
                    <td className="px-5 py-4" data-id="element-511" data-mp="34bhl3fjdy5me">
                      <div className="flex items-center gap-1" data-id="element-512" data-mp="27jw8e9r7j4g7">
                        <button onClick={() => openEditModal(purchase)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-513" data-mp="2bkpuqyp04b6z">
                      
                          <EditIcon className="w-4 h-4" data-id="element-514" data-mp="2gm5fepn9ofou" />
                        </button>
                        <button onClick={() => {
                    setEditingPurchase(purchase);
                    setIsDeleteModalOpen(true);
                  }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-515" data-mp="2lgsyg8zi4hw1">
                      
                          <TrashIcon className="w-4 h-4" data-id="element-516" data-mp="1lcs36r9ecl9u" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingPurchase ? 'Editar Compra' : 'Registrar Compra de Caja Vacía'} size="md" data-id="element-517" data-mp="16krn1lqr7sda">
        
        <ModalBody data-id="element-518" data-mp="xleb8mf5qrpe">
          <div className="space-y-4" data-id="element-519" data-mp="xkv29v7hjdni">
            <div className="grid grid-cols-2 gap-4" data-id="element-520" data-mp="xjth2xwr9as2">
              <div data-id="element-521" data-mp="sl8eiru16f0g">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-522" data-mp="smt8ogcfnvxs">
                  Farmacia
                </label>
                <select value={formData.farmacia} onChange={e => setFormData({
                ...formData,
                farmacia: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-523" data-mp="np1lfzmqgxzx">
                  
                  {FARMACIAS.map(f => <option key={f} value={f} data-id="element-524" data-mp="l2sd22w7zbv4">
                      {f}
                    </option>)}
                </select>
              </div>
              <div data-id="element-525" data-mp="6bjj5hlab4c6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-526" data-mp="bcpe7m2f33ov">
                  Vendedor
                </label>
                <select value={formData.vendedor} onChange={e => setFormData({
                ...formData,
                vendedor: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-527" data-mp="z20e71by0u1y">
                  
                  {VENDEDORES.map(v => <option key={v} value={v} data-id="element-528" data-mp="u59zahbvk4i4">
                      {v}
                    </option>)}
                </select>
              </div>
            </div>
            <Input label="Fecha" type="date" value={formData.fecha} onChange={e => setFormData({
            ...formData,
            fecha: e.target.value
          })} data-id="element-529" data-mp="3eu7khp2jk2n6" />
            
            <div className="grid grid-cols-2 gap-4" data-id="element-530" data-mp="32blrdjygnczr">
              <Input label="Cantidad de Cajas" type="number" value={formData.cantidadCajas} onChange={e => setFormData({
              ...formData,
              cantidadCajas: e.target.value
            })} placeholder="0" data-id="element-531" data-mp="32d6i0yhnmbsc" />
              
              <Input label="Precio por Caja (DOP)" type="number" value={formData.precioPorCaja} onChange={e => setFormData({
              ...formData,
              precioPorCaja: e.target.value
            })} placeholder="0" data-id="element-532" data-mp="2oimbvhyohis7" />
              
            </div>
            {/* Auto-calculated total */}
            {computedTotal > 0 && <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200" data-id="element-533" data-mp="2tgxkjpd8ln8u">
                <span className="text-sm font-medium text-slate-600" data-id="element-534" data-mp="25qtg5fs85jgs">
                  Total calculado:
                </span>
                <span className="text-lg font-bold text-slate-900" data-id="element-535" data-mp="1farshk3tteh5">
                  {formatCurrency(computedTotal)}
                </span>
              </div>}
            <div data-id="element-536" data-mp="1k9uz38zgfsr8">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-537" data-mp="1kbfu0zpoldpm">
                Estado
              </label>
              <select value={formData.estado} onChange={e => setFormData({
              ...formData,
              estado: e.target.value as EmptyBoxPurchase['estado']
            })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-538" data-mp="1ta1berjf2mot">
                
                <option value="pendiente" data-id="element-539" data-mp="27zpbjpfv3fay">Pendiente</option>
                <option value="pagado" data-id="element-540" data-mp="2cys5adqe5z3m">Pagado</option>
              </select>
            </div>
            <div data-id="element-541" data-mp="2h05car5808c4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-542" data-mp="2h0n6li8boche">
                Nota (opcional)
              </label>
              <input type="text" value={formData.nota} onChange={e => setFormData({
              ...formData,
              nota: e.target.value
            })} placeholder="Observaciones..." className="block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-543" data-mp="2lz877irhky6d" />
              
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-544" data-mp="3siss3sefeii4">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-545" data-mp="3sjko34bxqi8x">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.cantidadCajas || !formData.precioPorCaja} data-id="element-546" data-mp="p0hp7h924afe">
            
            {editingPurchase ? 'Guardar Cambios' : 'Registrar Compra'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Registro" size="sm" data-id="element-547" data-mp="f8vb9d5c3x8y">
        
        <ModalBody data-id="element-548" data-mp="17z3nkod6vnfm">
          <p className="text-slate-600" data-id="element-549" data-mp="17ylj54gdi2wm">
            ¿Eliminar la compra de <strong data-id="element-550" data-mp="1pmc1h3ztappf">{editingPurchase?.farmacia}</strong>?
            Esta acción no se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-551" data-mp="1kmps55n3ttlw">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-552" data-mp="1km7ohny44d3y">
            
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-553" data-mp="1apl2uwxypi0v">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}